import type { PayloadAction, Update } from '@reduxjs/toolkit'
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit'

import type {
  MultipartUploadedFile,
  MultipartUploadedFileSetupType,
  WellListItem,
} from '../../types'
import { defaultUploadedFileStepStatuses } from '../../types'

export const multipartUploadedFileAdapter = createEntityAdapter<MultipartUploadedFile>({
  selectId: file => file.fileId,
})

const initialState = multipartUploadedFileAdapter.getInitialState<{
  activeFile?: MultipartUploadedFile
  context: {
    wellsForMapping?: WellListItem[]
  }
}>({ context: {} })

export const multipartUploadedFileSlice = createSlice({
  name: 'multipartUploadedFile',
  initialState,
  reducers: {
    addMultipartFiles(state, { payload: files }: PayloadAction<MultipartUploadedFile[]>) {
      files.forEach(file => {
        file.statuses ??= getStatuses(file)
      })
      multipartUploadedFileAdapter.upsertMany(state, files)
    },
    setMultipartInitialSetup(
      state,
      { payload }: PayloadAction<{ fileId: string; fileSetupType: MultipartUploadedFileSetupType }>,
    ) {
      const uploadedFile = state.entities[payload.fileId]
      if (uploadedFile !== undefined) {
        uploadedFile.initialSetupContent = payload.fileSetupType
      }
    },
    removeMultipartFiles(state, { payload }: PayloadAction<string[]>) {
      const fileIds = payload
      multipartUploadedFileAdapter.removeMany(state, fileIds)
      if (state.activeFile && fileIds.includes(state.activeFile.fileId)) {
        state.activeFile = undefined
      }
    },
    setActiveMultipartFile(state, { payload: fileId }: PayloadAction<string | undefined>) {
      state.activeFile = !fileId ? undefined : state.entities[fileId]
    },
    clearMultipartFiles(state) {
      return { ...initialState, context: state.context }
    },
    updateMultipartState(state, action: PayloadAction<Update<MultipartUploadedFile>>) {
      multipartUploadedFileAdapter.updateOne(state, action.payload)
      const fileId = action.payload.id
      if (fileId === state.activeFile?.fileId) {
        state.activeFile = state.entities[fileId]
      }
    },
    updateMultipartContext(state, action: PayloadAction<typeof initialState.context>) {
      const payload = action.payload
      if (payload.wellsForMapping) {
        state.context.wellsForMapping = payload.wellsForMapping
      }
    },
  },
})

const getStatuses = (file: MultipartUploadedFile): MultipartUploadedFile['statuses'] => {
  switch (file.entityType) {
    case 'marker': {
      return {
        ...defaultUploadedFileStepStatuses,
        setup: 'inprogress',
        mapping: 'notstarted',
        loading: 'notstarted',
      }
    }
    default: {
      return {
        ...defaultUploadedFileStepStatuses,
        setup: 'inprogress',
        loading: 'notstarted',
        conversion: 'notstarted',
        ready: 'notstarted',
      }
    }
  }
}

export default multipartUploadedFileSlice.reducer
